import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import * as styles from './TopLevelTopicSettings.module.scss';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';

const UPDATE_ORGANIZATION_USER_ROOT_TOPIC_SETTINGS = gql(/** @complexity 4 */ `
  mutation updateOrganizationUserRootTopicSettings(
    $allowUserRootTopic: Boolean!
  ) {
    organization: updateOrganization(
      settings: { allowUserRootTopic: $allowUserRootTopic }
    ) {
      id
      settings {
        allowUserRootTopic
      }
    }
  }
`);

const ORGANIZATION_USER_ROOT_TOPIC_SETTINGS_QUERY = gql(/** @complexity 3 */ `
  query OrganizationUserRootTopicSettings {
    session: currentSession {
      organization {
        id
        settings {
          allowUserRootTopic
        }
      }
    }
  }
`);

const OPTIONS = {
  enabled: 'Users can make topics top level',
  disabled: 'Only admins can make topics top level',
} as const;

type Values = {
  allowUserRootTopic: keyof typeof OPTIONS;
};

const TopLevelTopicSettings = () => {
  const [isEditing, setIsEditing] = useState(false);

  const [updateSettings] = useMutation(
    UPDATE_ORGANIZATION_USER_ROOT_TOPIC_SETTINGS,
  );

  const { data } = useQuery(ORGANIZATION_USER_ROOT_TOPIC_SETTINGS_QUERY);
  if (!data) return null;

  const allowUserRootTopic = data.session.organization.settings
    ?.allowUserRootTopic
    ? 'enabled'
    : 'disabled';

  const handleSubmit = async (values: Values) => {
    await updateSettings({
      variables: {
        allowUserRootTopic: values.allowUserRootTopic === 'enabled',
      },
    });
    setIsEditing(false);
  };

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Top level topics"
          description={
            <Form<Values>
              initialValues={{ allowUserRootTopic }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(['enabled', 'disabled'] as const).map((value) => (
                  <Field
                    key={value}
                    label={OPTIONS[value]}
                    name="allowUserRootTopic"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.description}>
                Regardless of selection, guests cannot create top level topics.
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            <Button onClick={() => setIsEditing(true)} variant="secondary">
              Edit
            </Button>
          }
          title="Top level topics"
          description={OPTIONS[allowUserRootTopic]}
        />
      )}
    </>
  );
};

export default TopLevelTopicSettings;
